import Papa from 'papaparse';
import { io } from 'socket.io-client';
import { useSelector } from 'react-redux';
import React, { useCallback, Fragment, useState, useEffect } from 'react';

import {
    Box,
    List,
    Grid,
    ListItem,
    Typography,
    ListItemText,
    CircularProgress,
} from '@mui/material';

import {
    Button,
    DataTable,
    Animation,
    Autocomplete,
    CircleLoading,
    SnackBarAlert,
    TablePagination,
} from 'shared';
import {
    allowedStatuses,
    allowedFullfilment,
    expectedInvoiceHeaders,
} from '../../utils/fileHeaders';
import {
    isValidDate,
    statusOptions,
    sanitizePrice,
    extendedHeaders,
} from 'utils/fileValidationHelpers';
import { Upload } from 'components/upload';
import { handleErrorAlert } from 'utils/handleErrorAlert';
import { uploadFileTabStyles } from '../../styles/styles';
import { handleSuccessAlert } from 'utils/handleSuccessAlert';
import { useInvoicesService } from 'store/services/invoices.service';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const TOKEN = process.env.REACT_APP_SOCKET_TOKEN;

const UploadInvoicesFileTab = () => {
    const classes = uploadFileTabStyles();
    const companyData = useSelector((state) => state.company.data);
    const { addInvoiceFile, getInvoiceFiles } = useInvoicesService();

    const [page, setPage] = useState(1);
    const [rows, setRows] = useState([]);
    const [data, setData] = useState([]);
    const [files, setFiles] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [alertData, setAlertData] = useState({
        open: false,
        message: '',
        severity: '',
    });
    const [loading, setLoading] = useState(true);
    const [fileName, setFileName] = useState(null);
    const [showStatus, setShowStatus] = useState(false);
    const [statusOption, setStatusOption] = useState('all');
    const [importLoading, setImportLoading] = useState(false);
    const [processingFiles, setProcessingFiles] = useState(null);

    const handleFileDrop = useCallback(
        (acceptedFiles, rejectedFiles) => {
            if (rejectedFiles && rejectedFiles.length > 0) {
                const hasFileTypeError = rejectedFiles.some((rejection) =>
                    rejection.errors.some((error) => error.code === 'file-invalid-type'),
                );

                if (hasFileTypeError) {
                    handleErrorAlert(
                        setAlertData,
                        'Invalid file type. Please upload a CSV file (.csv format only).',
                    );
                    return;
                }
            }
            if (!acceptedFiles || acceptedFiles.length === 0) {
                return;
            }
            handleClear();
            const newFiles = acceptedFiles.map((file) =>
                Object.assign(file, {
                    preview: URL.createObjectURL(file),
                }),
            );
            const uploadedFile = newFiles[0];
            setFiles([uploadedFile]);
            if (newFiles) {
                setFileName(uploadedFile.name);
                Papa.parse(newFiles[0], {
                    header: true,
                    skipEmptyLines: true,
                    complete: (results) => {
                        if (results.data.length > 0) {
                            const firstRow = results.data[0];
                            const formatted = Object.keys(firstRow).map((header) => ({
                                id: header,
                                label: header,
                                sort: false,
                                verticalAlign: 'top',
                                changed: false,
                                labelForSelect: header,
                            }));
                            setHeaders(formatted);
                            setRows(results.data);
                            setData(results.data);
                        }
                    },
                    error: (error) => {
                        handleErrorAlert(setAlertData, 'Error parsing CSV, please try again');
                    },
                });
            }
        },
        [files],
    );

    const formatData = () => {
        const updatedRows = [];

        rows.map((row) => {
            const errors = [];

            for (const header of expectedInvoiceHeaders) {
                if (!(header in row)) {
                    errors.push(`Missing column from file: ${header}`);
                }
            }

            Object.keys(row).forEach((key) => {
                const mappedValue = row[key];

                const valueStr =
                    mappedValue !== undefined && mappedValue !== null
                        ? String(mappedValue).trim()
                        : '';

                if (key.toLowerCase() === 'customer') {
                    if (!valueStr) {
                        errors.push('customer is required');
                    }
                }

                if (key.toLowerCase() === 'invoice_id') {
                    if (!valueStr) {
                        errors.push('invoice_id is required');
                    }
                }

                if (key.toLowerCase() === 'currency') {
                    if (!valueStr) {
                        errors.push('currency is required');
                    }
                }

                if (key.toLowerCase() === 'amount') {
                    if (!valueStr) {
                        errors.push('amount is required');
                    } else {
                        const number = sanitizePrice(valueStr);
                        if (number === null || Number.isNaN(Number(number))) {
                            errors.push(`amount must be a valid number ("${valueStr}")`);
                        }
                    }
                }

                if (key.toLowerCase() === 'date') {
                    if (!valueStr) {
                        errors.push('date is required');
                    } else if (!isValidDate(valueStr)) {
                        errors.push(`Invalid date: "${valueStr}" (expected MM-DD-YYYY)`);
                    }
                }

                if (key.toLowerCase() === 'event_date') {
                    if (!valueStr) {
                        errors.push('event_date is required');
                    } else if (!isValidDate(valueStr)) {
                        errors.push(`Invalid event_date: "${valueStr}" (expected MM-DD-YYYY)`);
                    }
                }

                if (key.toLowerCase() === 'external_refernce') {
                    if (!valueStr) {
                        errors.push('external_refernce is required');
                    }
                }

                if (key.toLowerCase() === 'performer') {
                    if (!valueStr) {
                        errors.push('performer is required');
                    }
                }

                if (key.toLowerCase() === 'status') {
                    if (!valueStr) {
                        errors.push('status is required');
                    } else if (!allowedStatuses.includes(valueStr.toLowerCase())) {
                        errors.push(
                            `Invalid status: "${valueStr}" (must be one of [${allowedStatuses.join(
                                ', ',
                            )}]).`,
                        );
                    }
                }

                if (key.toLowerCase() === 'fullfilment') {
                    if (!valueStr) {
                        errors.push('fullfilment is required');
                    } else if (!allowedFullfilment.includes(valueStr.toLowerCase())) {
                        errors.push(
                            `Invalid fullfilment: "${valueStr}" (must be one of [${allowedFullfilment.join(
                                ', ',
                            )}]).`,
                        );
                    }
                }
            });

            const status = {
                valid: errors.length === 0,
                issues: errors,
            };
            updatedRows.push({ ...row, _status: status });
        });

        setRows(updatedRows);
        setData(updatedRows);
        return updatedRows;
    };

    const handleClear = () => {
        setPage(1);
        setRows([]);
        setData([]);
        setFiles([]);
        setHeaders([]);
        setFileName(null);
        setShowStatus(false);
        setStatusOption('all');
    };

    const handleImport = async () => {
        const data = formatData();

        const invalidRows = data.filter((row) => !row._status.valid);

        if (invalidRows.length > 0) {
            handleErrorAlert(
                setAlertData,
                `${invalidRows.length} row(s) have errors. Please fix them before importing`,
            );
            setShowStatus(true);
            return;
        } else {
            setShowStatus(false);
        }

        setImportLoading(true);

        try {
            const response = await addInvoiceFile({
                data,
                company_id: companyData.id,
                fileName,
            });
            handleSuccessAlert(setAlertData, response.message);
            handleClear();
            handleGetInvoiceFiles();
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setImportLoading(false);
        }
    };

    const handleStatusChange = (e, status) => {
        setStatusOption(status.value);
        setPage(1);
        if (status.value === 'all') {
            setData(rows);
        } else if (status.value === 'valid') {
            setData(rows?.filter((row) => row?._status?.valid));
        } else if (status.value === 'invalid') {
            setData(rows?.filter((row) => !row?._status?.valid));
        }
    };

    const handleGetInvoiceFiles = async () => {
        try {
            setLoading(true);
            const response = await getInvoiceFiles({
                companyId: companyData?.id,
                status: 'processing',
            });
            setProcessingFiles(response?.data);
        } catch (error) {
            handleErrorAlert(setAlertData);
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const link = document.createElement('a');
        link.href = '/assets/files/invoices-template.csv';
        link.download = 'Invoices Templates.csv';
        link.click();
    };

    useEffect(() => {
        if (!companyData) return;

        const socket = io(SOCKET_URL, {
            transports: ['websocket'],
            path: '/api/socket/',
            query: {
                token: TOKEN,
            },
        });

        const eventName = `invoice-file/${companyData.id}/`;

        socket.off(eventName);
        socket.on(eventName, (data) => {
            handleSuccessAlert(setAlertData, `${data?.file_name} processed successfully`);

            setProcessingFiles((prev) => prev.filter((f) => f.id !== data.id));
        });

        socket.on('connect_error', (error) => {
            console.error('Connection error:', error);
        });

        return () => {
            socket.disconnect();
        };
    }, [companyData, processingFiles]);

    useEffect(() => {
        if (companyData) {
            handleGetInvoiceFiles();
        }
    }, [companyData]);

    return (
        <Fragment>
            {loading || !companyData ? (
                <CircleLoading />
            ) : (
                <Fragment>
                    <Grid container marginBottom="20px" justifyContent="flex-end">
                        <Button
                            text="DOWNLOAD INVOICES CSV TEMPLATE"
                            customStyles={classes.button}
                            onClick={handleDownloadTemplate}
                        />
                    </Grid>
                    <Grid container sx={classes.wrapper}>
                        {processingFiles && processingFiles?.length > 0 && (
                            <Grid item sx={12} width="100%" marginBottom={2}>
                                <Box margin="auto" padding={2} borderRadius={2} boxShadow={3}>
                                    <Typography variant="h6" marginBottom={2}>
                                        Processing Files
                                    </Typography>
                                    <List>
                                        {processingFiles.map((file) => (
                                            <ListItem
                                                justifyContent="space-between"
                                                display="flex"
                                                key={file.id}
                                            >
                                                <ListItemText primary={file.file_name} />
                                                <CircularProgress size={24} />
                                            </ListItem>
                                        ))}
                                    </List>
                                </Box>
                            </Grid>
                        )}
                        <Fragment>
                            <Grid item xs={12} width={'100%'}>
                                <Box data-tour="upload-area">
                                    <Upload
                                        multiple
                                        accept={{
                                            '.csv': [],
                                        }}
                                        placeholder={'Drag and drop your files here (CSV)'}
                                        files={files}
                                        onDrop={handleFileDrop}
                                        onRemove={handleClear}
                                        data-tour="upload-area"
                                    />
                                </Box>
                                {headers.length > 0 && (
                                    <Box sx={classes.buttonsWrapper}>
                                        <Box data-tour="import-button">
                                            <Button
                                                text="Import"
                                                variant="outlined"
                                                customStyles={classes.button}
                                                onClick={handleImport}
                                                loading={importLoading}
                                            />
                                        </Box>

                                        <Button
                                            text="Clear"
                                            onClick={handleClear}
                                            customStyles={classes.clearButton}
                                        />
                                    </Box>
                                )}
                            </Grid>
                            {files.length > 0 && (
                                <Grid container marginTop={5} spacing={2}>
                                    <Grid item sm={12} md={6}>
                                        <Typography marginBottom="20px">
                                            Total Rows: {rows.length}
                                        </Typography>
                                        {showStatus && (
                                            <Typography marginBottom="20px" color="error">
                                                Total Rows with error:{' '}
                                                {rows?.filter((row) => !row?._status?.valid).length}
                                            </Typography>
                                        )}
                                    </Grid>
                                </Grid>
                            )}
                        </Fragment>
                    </Grid>

                    {headers.length > 0 && (
                        <Grid container marginTop="10px" sx={classes.wrapper}>
                            {showStatus && (
                                <Grid item xs={6}>
                                    <Autocomplete
                                        options={statusOptions}
                                        labelKey="label"
                                        valueKey="value"
                                        inlineLabel="Status"
                                        placeholder="Select by Status"
                                        value={statusOptions.find(
                                            (type) => type.value == statusOption,
                                        )}
                                        onChange={handleStatusChange}
                                        disableClearable
                                    />
                                </Grid>
                            )}
                            <Grid item xs={12} marginTop="5px">
                                <DataTable
                                    headCells={extendedHeaders(headers, showStatus)}
                                    data={data.slice((page - 1) * 10, page * 10)}
                                ></DataTable>
                            </Grid>
                            <Grid item xs={12} marginTop="5px">
                                <TablePagination
                                    page={page}
                                    count={data.length}
                                    onChange={(e, pageNumber) => setPage(pageNumber)}
                                />
                            </Grid>
                        </Grid>
                    )}

                    {importLoading && <Animation title="Uploading your invoices data" />}

                    <SnackBarAlert alertData={alertData} setAlertData={setAlertData} />
                </Fragment>
            )}
        </Fragment>
    );
};

export default UploadInvoicesFileTab;
